import { SIDE_BAR_LIMIT } from "config/appConfig";
import { GetAllBlocks } from "hooks/apollo/blocks/block.gql";
import { GetAllLines } from "hooks/apollo/line/line.gql";
import { GetWorkOrder, GetWorkOrders } from "hooks/apollo/workOrder/workOrder.gql";
import cloneDeep from "lodash/cloneDeep";
import filter from "lodash/filter";

function insertWorkBlock(newWorkBlock, proxy, getAllBlocksVariables) {
  const completeNewWorkBlock = prepareWorkBlock(newWorkBlock, proxy);
  const { blocks } = read(proxy, GetAllBlocks, getAllBlocksVariables);

  const workBlocks = [completeNewWorkBlock].concat(filter(blocks.workBlocks, (w) => w.id !== newWorkBlock.id));

  write({ blocks: { ...blocks, workBlocks } }, proxy, GetAllBlocks, getAllBlocksVariables);

  insertWorkBlockToVisibleWorkOrder(completeNewWorkBlock, proxy);
}

function removeWorkBlocksForWorkOrder(workOrderId, proxy, getAllBlocksVariables) {
  const { blocks } = read(proxy, GetAllBlocks, getAllBlocksVariables);

  const workBlocks = blocks.workBlocks.filter((workBlock) => workBlock.workOrder.id !== workOrderId);

  write({ blocks: { ...blocks, workBlocks } }, proxy, GetAllBlocks, getAllBlocksVariables);

  removeAllWorkBlocksFromVisibleWorkOrder(workOrderId, proxy);
}

function removeWorkBlock(workBlockId, proxy, getAllBlocksVariables) {
  const { blocks } = read(proxy, GetAllBlocks, getAllBlocksVariables);

  if (blocks?.workBlocks?.length === 0) return;

  const { workBlock, workBlocks } = blocks.workBlocks.reduce(
    (acc, wb) => {
      if (wb.id === workBlockId) acc.workBlock = wb;
      else acc.workBlocks.push(wb);
      return acc;
    },
    { workBlock: null, workBlocks: [] },
  );

  write({ blocks: { ...blocks, workBlocks } }, proxy, GetAllBlocks, getAllBlocksVariables);

  removeWorkBlockFromVisibleWorkOrder(workBlock, proxy);
}

function insertWorkBlockToVisibleWorkOrder(completeNewWorkBlock, proxy) {
  const cachedValue = read(proxy, GetWorkOrder, {
    id: completeNewWorkBlock.workOrder.id,
  });

  if (cachedValue?.workOrder) {
    const workOrderCopy = cloneDeep(cachedValue.workOrder);
    if (filter(workOrderCopy.workBlocks, (w) => w.id === completeNewWorkBlock.id).length === 0) {
      workOrderCopy.workBlocks.push(completeNewWorkBlock);
      write({ workOrder: workOrderCopy }, proxy, GetWorkOrder, {
        id: completeNewWorkBlock.workOrder.id,
      });
    }
  }
}

function removeAllWorkBlocksFromVisibleWorkOrder(workOrderId, proxy) {
  const cachedValue = read(proxy, GetWorkOrder, {
    id: workOrderId,
  });

  if (cachedValue?.workOrder) {
    const workOrderCopy = cloneDeep(cachedValue.workOrder);

    workOrderCopy.workBlocks = [];
    write({ workOrder: workOrderCopy }, proxy, GetWorkOrder, {
      id: workOrderId,
    });
  }
}

function removeWorkBlockFromVisibleWorkOrder(workBlock, proxy) {
  if (!workBlock) return;

  const cachedValue = read(proxy, GetWorkOrder, {
    id: workBlock.workOrder.id,
  });

  if (cachedValue?.workOrder) {
    const workOrderCopy = cloneDeep(cachedValue.workOrder);
    workOrderCopy.workBlocks = workOrderCopy.workBlocks.filter((wb) => wb.id !== workBlock.id);
    write({ workOrder: workOrderCopy }, proxy, GetWorkOrder, {
      id: workBlock.workOrder.id,
    });
  }
}

function prepareWorkBlock(workBlock, proxy) {
  return {
    ...workBlock,
    line: getLine(workBlock.line.id, proxy) || workBlock.line,
    workOrder: getWorkOrder(workBlock.workOrder.id, proxy) || workBlock.workOrder,
  };
}

function getLine(lineId, proxy) {
  const { lines } = read(proxy, GetAllLines, { activeOnly: true });

  return lines.find((line) => line.id === lineId);
}

function getWorkOrder(workOrderId, proxy) {
  const { workOrders } = read(proxy, GetWorkOrders, { limit: SIDE_BAR_LIMIT });

  return workOrders.find((workOrder) => workOrder.id === workOrderId);
}

function read(proxy, query, variables = null) {
  return proxy.readQuery({ query, returnPartialData: true, variables });
}

function write(data, proxy, query, variables = null) {
  proxy.writeQuery({
    data,
    query,
    variables,
  });
}

export { insertWorkBlock, removeWorkBlock, removeWorkBlocksForWorkOrder };
